import { useEffect, useState } from 'react';
import { localStore } from '../platform/browser.js';
import { addDays, formatDayName, toISODate, todayISO } from '@mononoke/shared';
import { AreaChip } from '../components/area-chip.js';
import { api, call } from '../platform/api.js';
import { navigate, type Route } from '../platform/router.js';
import type { DashboardCounts } from '../../../api/src/catalog/dashboard.js';
import type { ActivityCursor, ActivityRow } from '../../../api/src/catalog/activity.js';
import { t, tStatus } from '../platform/i18n.js';
import { useLanguage } from '../platform/use-language.js';

/**
 * "5d" — the COMPACT age the frame draws (13-2a, kept by 16a), a single right-aligned token
 * beside each activity row rather than a sentence ("5 days ago"). now / 5m / 3h / 5d.
 *
 * ⚠️ COMPACT ENGLISH, not `Intl.RelativeTimeFormat`, since 0.1.308: the frame's slot is one
 * short token and Intl has no compact style. The app ships English (Dutch is on hold); if it
 * returns, this wants a compact-localised form rather than the sentence Intl gives.
 *
 * ⚠️ THIS IS NOT A CALENDAR-DATE DERIVATION, so the project's date rule does not apply and
 * `new Date(iso)` here is correct. That rule exists because a DAY is a local concept and
 * `toISOString().slice(0,10)` silently answers in UTC — Brussels evenings landing on
 * tomorrow. What is being measured here is the distance between two INSTANTS, which has no
 * timezone in it at all. Said out loud because the lint rule and the habit both point the
 * other way, and the next reader will rightly look twice.
 */
function relativeTime(iso: string, now: number): string {
  const then = new Date(iso).getTime();
  // A future instant is possible: migration 017 honours a client clock up to five minutes
  // ahead. Clamping to 0 renders it as "now" rather than "in 3 minutes", which would look
  // like a fault in the app rather than a phone with a fast clock.
  const seconds = Math.max(0, Math.round((now - then) / 1000));
  if (seconds < 60) return 'now';
  if (seconds < 3600) return `${String(Math.round(seconds / 60))}m`;
  if (seconds < 86400) return `${String(Math.round(seconds / 3600))}h`;
  return `${String(Math.round(seconds / 86400))}d`;
}

/** The four statuses, in the order the rows and the meters both use — one order, one truth. */
const STATUSES = ['available', 'checked_out', 'in_custody', 'flagged'] as const;
type Status = (typeof STATUSES)[number];
const STATUS_ROUTE = (status: Status): Route => ({ name: 'items', status: [status] });
/** The meter's tone per status — the same four the old bar's segments wore. */
const STATUS_SEG: Record<Status, string> = {
  available: 'dash-seg-available',
  checked_out: 'dash-seg-out',
  in_custody: 'dash-seg-custody',
  flagged: 'dash-seg-flagged',
};

/** A status as a row name: `tStatus` answers in the vocabulary's lowercase ("checked out"),
 *  a row name starts with a capital. One place, so the four rows agree. */
function statusName(status: Status): string {
  const s = tStatus(status);
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** The chevron every list row ends with — the door. A stroked SVG, like the rail's (0.1.444). */
function Chevron(): JSX.Element {
  return (
    <svg className="dash-chev" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M6 3.5 10.5 8 6 12.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * A GROUP RAIL that is a heading, not a toggle. The dashboard's three sections wear the rail
 * every grouped sheet uses (`.ds-rail`, 0.1.444/0.1.457) so the screen reads like the rest of
 * the app — but nothing collapses here, so it is a `<div>` with an `<h3>` inside, never the
 * `GroupRail` button: a rail that looks tappable and does nothing is a lie on a phone.
 *
 * ⚠️ FRAME 16a: the rail sits on `--surface` with the DOT and NAME in the tone — not the
 * tone's soft band the data sheets draw since 0.1.457. `.dash-rail` overrides the band;
 * the reason is that these three are fixed sections of one screen, not groups of a list,
 * and three tinted bands stacked on a 390px screen read as three alerts.
 */
function Rail({
  tone,
  name,
  count,
  hint,
  open,
  onToggle,
}: {
  tone: 'alert' | 'ok' | 'inert';
  name: string;
  count?: string | number | undefined;
  hint?: string | undefined;
  open: boolean;
  onToggle: () => void;
}): JSX.Element {
  // THE RAIL FOLDS (0.1.526; maintainer, 2 Oct 2026: "make the subsections in the dashboard tab
  // expandable and collapsible"). The whole rail is the control, inside its <h3> so the headings
  // outline holds — the project bands' shape (`CollapsibleSection`), on the dashboard's own rail
  // look (frame 16a), with the same ⌃/⌄ glyphs.
  return (
    <h3 className="dash-rail-heading">
      <button
        type="button"
        className={`ds-rail ds-rail-0 ds-rail-tone-${tone} dash-rail`}
        aria-expanded={open}
        onClick={onToggle}
      >
        <span className="ds-rail-dot" aria-hidden="true" />
        <span className="ds-rail-name">{name}</span>
        {count !== undefined && <span className="ds-rail-count">{count}</span>}
        {hint !== undefined && <span className={`dash-rail-hint${tone === 'ok' ? ' dash-rail-hint-ok' : ''}`}>{hint}</span>}
        <span className="dash-rail-chevron" aria-hidden="true">
          {open ? '⌃' : '⌄'}
        </span>
      </button>
    </h3>
  );
}

/** The three rails' folds, remembered on this device — a per-viewer convenience, every access
 *  guarded: a blocked or unreadable store just means everything open. */
type DashFold = 'action' | 'status' | 'activity';
const FOLD_KEY = 'mononoke.dashboard-folds.v1';
function readDashFolds(): ReadonlySet<DashFold> {
  try {
    const parsed: unknown = JSON.parse(localStore?.getItem(FOLD_KEY) ?? '[]');
    return new Set(
      Array.isArray(parsed) ? parsed.filter((k): k is DashFold => k === 'action' || k === 'status' || k === 'activity') : [],
    );
  } catch {
    return new Set();
  }
}
function writeDashFolds(folded: ReadonlySet<DashFold>): void {
  try {
    localStore?.setItem(FOLD_KEY, JSON.stringify([...folded]));
  } catch {
    // A full or blocked store only forgets the choice; the fold itself already happened.
  }
}

/**
 * The landing screen (parity increment 3; ruling of 28 Jul: dashboard at `/`, the
 * item list one tap away at /items). A status wall in Cheqroom's sense: every number
 * answers part of "what needs me today?", and every number IS A LINK to the screen
 * where the answer can be acted on — the counts come from those screens' own reads
 * (see catalog/dashboard.ts), so row and screen cannot disagree.
 *
 * REDRAWN AT 0.1.493 as direction A / A3 of the "Mononoke Dashboard" canvas (frames 16a, 16b;
 * maintainer, 8 Sep 2026): the screen in the app's own list vocabulary and nothing else — three
 * rails, 48px rows, the count where a date would sit, a 3px edge for state, a 4px meter for
 * proportion. On the desk the two short lists share the top band and the activity ledger takes
 * the full width beneath, 24px below them (A3). One DOM for both widths; CSS chooses the shape.
 */
export function DashboardScreen(): JSX.Element {
  useLanguage();
  const [counts, setCounts] = useState<DashboardCounts | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [activity, setActivity] = useState<ActivityRow[] | null>(null);
  /** The feed's own failure, kept APART from `problem`: the counts are the reason this
   * screen exists, and a feed that could not load must not blank them. */
  const [activityProblem, setActivityProblem] = useState<string | null>(null);
  /**
   * "Now" is captured ONCE per render pass rather than read inside each row. Calling
   * `Date.now()` per row would make two rows written in the same second disagree about
   * their own age, and would re-tick mid-list on a slow render.
   */
  const [now] = useState(() => Date.now());
  const [folded, setFolded] = useState<ReadonlySet<DashFold>>(readDashFolds);
  const isOpen = (k: DashFold): boolean => !folded.has(k);
  const toggle = (k: DashFold) => (): void =>
    setFolded((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      writeDashFolds(next);
      return next;
    });

  useEffect(() => {
    void (async () => {
      const result = await call('dashboard', () => api.dashboard.query());
      if (result.ok) setCounts(result.data);
      else setProblem(result.failure.message);
    })();
  }, []);

  /**
   * A SECOND, INDEPENDENT READ. The counts render as soon as they arrive and never wait
   * for the feed, which is the slower query — it joins three tables across two
   * append-only logs. Two effects rather than one for the same reason the procedures are
   * separate: a failure here costs the panel, never the status wall.
   *
   * An older API has no `recentActivity` procedure at all. The web bundle reaches
   * Cloudflare in seconds and the API reaches Fly minutes later, so that window happens on
   * EVERY release (0.1.88) — the panel simply reports its own failure and the rest of the
   * screen is untouched.
   */
  /**
   * PAGED SINCE 0.1.527 (`activityPage`, 100 rows a page across the 30-day window; "Load more"
   * appends the next). The old feed was the newest 20 rows and nothing more — a check-out
   * followed by a burst of edits fell off the end with no way back to it (maintainer, 2 Oct
   * 2026). A failed NEXT page keeps what is shown and says so under it (G10); it never blanks.
   */
  const [activityNext, setActivityNext] = useState<ActivityCursor | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreProblem, setMoreProblem] = useState<string | null>(null);
  useEffect(() => {
    void (async () => {
      const result = await call('activityPage', () => api.activityPage.query({ before: null }));
      if (result.ok) {
        setActivity(result.data.rows);
        setActivityNext(result.data.next);
      } else setActivityProblem(result.failure.message);
    })();
  }, []);
  const loadMore = async (): Promise<void> => {
    if (activityNext === null || loadingMore) return;
    setLoadingMore(true);
    setMoreProblem(null);
    const result = await call('activityPage', () => api.activityPage.query({ before: activityNext }));
    setLoadingMore(false);
    if (!result.ok) {
      setMoreProblem(result.failure.message);
      return;
    }
    setActivity((prev) => [...(prev ?? []), ...result.data.rows]);
    setActivityNext(result.data.next);
  };

  const today = todayISO();

  /**
   * A "NEEDS ACTION" ROW: the name, the count 20/700 at the right where a list row keeps its
   * date, the chevron; urgency is the 3px edge (rule 6) — no fill, no icon. Rows render ONLY
   * when their count is above zero: four rows reading 0 teach the eye nothing, and the quiet
   * day is the rail alone (rule 7; ruling R1, 8 Sep 2026).
   *
   * A `null` count (the database has not had that feature's migration) also renders no
   * row: an em dash has nothing to open (0.1.45's rule, applied to rows).
   *
   * THE "TO HAND OUT" COUNT STAYS UNDRAWN (maintainer, 5 Aug 2026) — computed by the
   * shared read, shown nowhere, exactly as before.
   */
  const actionRow = (count: number | null, label: string, route: Route, edge: 'alert' | 'warn' | 'none'): JSX.Element | null => {
    if (count === null || count === 0) return null;
    return (
      <li key={label}>
        <button type="button" className={`dash-row row-edge-${edge}`} aria-label={`Open ${label}`} onClick={() => navigate(route)}>
          <span className="dash-row-name">{label}</span>
          <strong className="dash-row-n">{count}</strong>
          <Chevron />
        </button>
      </li>
    );
  };
  const actionRows =
    counts === null
      ? []
      : [
          actionRow(counts.overdueBookings, t('dashboard.overdueReservations'), { name: 'checkouts' }, 'alert'),
          actionRow(counts.lenderReturnsSoon, t('dashboard.lenderReturnsWeek'), { name: 'boxes' }, 'warn'),
          actionRow(counts.clientsToChase, t('deadlines.chasing'), { name: 'checkouts' }, 'none'),
          actionRow(counts.maintenanceOpen, t('dashboard.openRepairs'), { name: 'maintenance' }, 'none'),
        ].filter((r): r is JSX.Element => r !== null);
  const quiet = counts !== null && actionRows.length === 0;

  const total = counts === null ? null : STATUSES.reduce((n, k) => n + (counts.statuses[k] ?? 0), 0);

  /**
   * THE LEDGER, GROUPED BY DAY (ruling R2, 8 Sep 2026: day rails on BOTH widths — one code
   * path). The day KEY is the calendar date of the instant in the atelier's zone, through the
   * shared helper: `occurredAt` is an instant, and a Brussels evening must land on its own day,
   * not on tomorrow's (the date rule). "Today" and "Yesterday" by name, every other day as the
   * weekday and date. The server pages the 30-day window newest first (0.1.527), so the groups
   * arrive in order — a page boundary inside a day just extends that day's group — and nothing
   * is sorted here.
   */
  const yesterday = addDays(today, -1);
  const dayLabel = (iso: string): string =>
    iso === today ? t('dashboard.today') : iso === yesterday ? t('dashboard.yesterday') : formatDayName(iso);
  const days: { iso: string; rows: ActivityRow[] }[] = [];
  for (const row of activity ?? []) {
    const iso = toISODate(new Date(row.occurredAt));
    const last = days[days.length - 1];
    if (last !== undefined && last.iso === iso) last.rows.push(row);
    else days.push({ iso, rows: [row] });
  }

  return (
    <section className="dash">
      <div className="screen-head board-head dash-head">
        <AreaChip area="dashboard">
          {/* The day beside the title (8b/8d): the dashboard answers "what needs doing
              TODAY", so the band names the day — through the shared helper, never a raw
              `new Date()` getter (the Brussels-evening rule). */}
          <span className="screen-head-sub">{formatDayName(today)}</span>
        </AreaChip>
      </div>
      {problem !== null && <p role="alert">{problem}</p>}

      {/* THE LEDGER IS THE SCROLLER, not the body (0.1.494 — maintainer, 8 Sep 2026: "I'm
          scrolling the whole page down when I want to scroll down in the recent activity").
          `.ds-wrap` is what the full-bleed desk pane scrolls (0.1.487); it sits on the ledger
          below, so the band and the two count lists stay put and only the day-book moves, with
          the sheet's own scrollbar. This wrapper is one link of the height chain
          (`.app-main-ds :has(.ds-wrap)`). Inert on a phone, where the page scrolls. */}
      <div className="dash-body">
        {/* A3: the two short lists share the top band on the desk; on a phone they stack. */}
        <div className="dash-top">
          <div className="dash-group">
            <Rail
              tone={quiet ? 'ok' : 'alert'}
              name={t('dashboard.needsAction')}
              count={counts === null ? undefined : actionRows.length}
              hint={quiet ? t('dashboard.nothingToday') : undefined}
              open={isOpen('action')}
              onToggle={toggle('action')}
            />
            {isOpen('action') && counts === null && problem === null && (
              <p role="status" className="dash-status">
                {t('common.loading')}
              </p>
            )}
            {isOpen('action') && actionRows.length > 0 && <ul className="dash-rows">{actionRows}</ul>}
          </div>

          <div className="dash-group">
            <Rail
              tone="ok"
              name={t('dashboard.itemsByStatus')}
              count={total === null ? undefined : total.toLocaleString('en-GB')}
              hint={total === null ? undefined : t('dashboard.garments')}
              open={isOpen('status')}
              onToggle={toggle('status')}
            />
            {isOpen('status') && counts !== null && (
              <ul className="dash-rows">
                {STATUSES.map((k) => {
                  const n = counts.statuses[k];
                  const known = n !== null && total !== null && total > 0;
                  return (
                    <li key={k}>
                      <button
                        type="button"
                        className="dash-row dash-row-tall"
                        aria-label={`Open ${statusName(k)}`}
                        onClick={() => navigate(STATUS_ROUTE(k))}
                      >
                        <span className="dash-row-main">
                          <span className="dash-row-name">{statusName(k)}</span>
                          {/* The meter: this status's share of the catalogue, 4px, never the
                              only signal — the count beside it is the fact. */}
                          <span className={`dash-meter ${STATUS_SEG[k]}`} aria-hidden="true">
                            <span style={{ width: known ? `${String(((n ?? 0) / (total ?? 1)) * 100)}%` : '0%' }} />
                          </span>
                        </span>
                        <strong className="dash-row-n">{n === null ? '—' : n.toLocaleString('en-GB')}</strong>
                        <Chevron />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        <div className="ds-wrap dash-ledger">
          <Rail
            tone="inert"
            name={t('dashboard.recentActivity')}
            hint={t('dashboard.recentActivityHint')}
            open={isOpen('activity')}
            onToggle={toggle('activity')}
          />
          {/* A failure still shows while folded — a fold hides content, never a problem (G10). */}
          {activityProblem !== null && <p role="alert">{activityProblem}</p>}
          {isOpen('activity') && activity === null && activityProblem === null && (
            <p role="status" className="dash-status">
              {t('common.loading')}
            </p>
          )}
          {isOpen('activity') && activity !== null && activity.length === 0 && <p className="dash-status">{t('dashboard.recentActivityEmpty')}</p>}
          {isOpen('activity') && days.length > 0 && (
            <>
              {/* The column heads exist on the desk only (CSS): act · garment · detail · who · when,
                  the Users table's 11/700 head on a `--border-strong` rule. */}
              <div className="dash-ledger-head ds-head" aria-hidden="true">
                <span className="ds-head-cell">{t('dashboard.ledger.act')}</span>
                <span className="ds-head-cell">{t('dashboard.ledger.garment')}</span>
                <span className="ds-head-cell">{t('dashboard.ledger.detail')}</span>
                <span className="ds-head-cell">{t('dashboard.ledger.who')}</span>
                <span className="ds-head-cell dash-ledger-when">{t('dashboard.ledger.when')}</span>
              </div>
              {days.map((day) => (
                <div key={day.iso} className="dash-day">
                  <div className="ds-rail ds-rail-1 dash-rail dash-day-rail">
                    <span className="ds-rail-name">{dayLabel(day.iso)}</span>
                    <span className="ds-rail-count">{day.rows.length}</span>
                  </div>
                  <ul className="dash-rows">
                    {day.rows.map((row, i) => (
                      <li key={`${row.kind}-${row.occurredAt}-${String(i)}`} className={`dash-act${row.what === 'flagged' ? ' row-edge-alert' : ' row-edge-none'}`}>
                        {/* The movement kind is rendered exactly as the item's own History panel
                            renders it — underscores to spaces, untranslated. Two screens showing
                            the same row must not word it differently, and translating one of them
                            is a vocabulary decision for both, not a side effect of this panel. */}
                        <span className="dash-act-what">{row.kind === 'edit' ? row.what : row.what.replace(/_/g, ' ')}</span>
                        {row.itemId !== null && row.itemName !== null ? (
                          <a
                            className="dash-act-item"
                            href={`/items/${row.itemId}`}
                            onClick={(e) => {
                              e.preventDefault();
                              navigate({ name: 'item', id: row.itemId! });
                            }}
                          >
                            {row.itemName}
                          </a>
                        ) : (
                          <span className="dash-act-item" />
                        )}
                        {/* `detail` is an edit's before → after; a movement has none and the cell
                            stays empty — a column of dashes would be noise, not "unanswered". */}
                        <span className="dash-act-detail">{row.detail ?? ''}</span>
                        <span className="dash-act-who">{row.actorName ?? t('dashboard.someone')}</span>
                        <time className="dash-act-when" dateTime={row.occurredAt}>
                          {relativeTime(row.occurredAt, now)}
                        </time>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
              {moreProblem !== null && <p role="alert">{moreProblem}</p>}
              {activityNext !== null && (
                <p className="dash-more">
                  <button type="button" className="chip" disabled={loadingMore} onClick={() => void loadMore()}>
                    {loadingMore ? t('dashboard.loadingMore') : t('dashboard.loadMore')}
                  </button>
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
}
